import { LocationDescriptorObject }              from 'history';
import { Uuid }                                  from './aliases';
import { AssumptionIdOrFieldName, ENTITY_TYPES } from './system';
import { UserContextType }                       from '../views/pages/users/UserIndexPage';


export interface RouterLocationState {
    referrer: LocationDescriptorObject | null | undefined,
    from?: string | null | undefined
}

export enum ROUTING_INTENTS {
    TARGET_ENTITY
}

export declare module RouterLocationStates {

    interface IntentBasedRouteState extends RouterLocationState {
        intent?: ROUTING_INTENTS;
    }

    interface PushToEntityBasedRouteState extends RouterLocationState {
        id: Uuid,
        type: ENTITY_TYPES,
        path: string,
        viewState?: unknown,
    }

    interface PushToDEMObjectiveBasedRouteState extends PushToEntityBasedRouteState {
        viewState: {
            tab: number
        };
    }

    interface Survey extends RouterLocationState {
        questionId?: AssumptionIdOrFieldName;
    }

    interface ObjectiveVisualization extends RouterLocationState {
        activeCategories?: string,
        activeFilters?: object,
    }

    interface ActivityVisualization extends RouterLocationState {
        focusDate?: string,
        activeCategories?: string,
        activeFilters?: object,
    }

    interface ActivityGanttVisualization extends RouterLocationState {
        focusStartDate?: string,
        focusEndDate?: string,
        activeCategories?: string,
        activeFilters?: object,
    }

    interface ProjectManagementSettings extends RouterLocationState {
        isEditing?: boolean,
    }
}

export enum AdminObjectiveManagerParamsBySection {
    BY_CATEGORY = 'by-category',
    BY_GROUP    = 'by-group',
    GROUPS      = 'groups',
    BY_NAME     = 'by-name'
}

export enum AdminActivityManagerParamsBySection {
    BY_CATEGORY  = 'by-category',
    BY_OBJECTIVE = 'by-objective',
    BY_NAME      = 'by-name',
}

export enum AdminResourceManagerTypeParams {
    PROFESSIONAL_FEES  = 'professional-fees',
    HIRE_TYPES         = 'hire-types',
    BUSINESS_FUNCTIONS = 'business-functions',
}

export enum AdminHireTypeManagerParamsBySection {
    BY_FUNCTION      = 'by-function',
    BY_NAME          = 'by-name',
    BY_RESOURCE_CODE = 'by-code',
}

export enum AdminProfessionalFeeManagerParamsBySection {
    BY_NAME          = 'by-name',
    BY_RESOURCE_CODE = 'by-code',
}

export enum AdminBusinessFunctionManagerParamsBySection {
    BY_NAME     = 'by-name',
    BY_REF_CODE = 'by-code',
}

export enum AdminTimingManagerTypeParams {
    MILESTONES = 'milestones',
}
export enum AdminMilestoneManagerParamsBySection {
    BY_NAME          = 'by-name',
    BY_RESOURCE_CODE = 'by-code',
}

export enum ObjectiveParamsSelectionSetType {
    CATEGORY = 'category',
    ALL      = 'all'
}

export enum ObjectiveParamsReviewSection {
    BY_PROGRESS = 'progress',
    BY_CATEGORY = 'categories',
    BY_YEAR     = 'yearly',
    BY_GROUP    = 'groups',
}

export enum PlanningAreaParamsSection {
    QUESTIONS_BY_CATEGORY          = 'planning-questions',
    ASSUMPTIONS_SUMMARY            = 'summary',
    PROJECT_DETAILS                = 'project-details',
    PROJECT_KEY_MILESTONES         = 'milestones',
    OBJECTIVE_SUMMARY_REVIEW       = 'objective-summary-review',
    WORKFLOW_ONBOARDING            = 'workflows-onboarding',
    WORKFLOW_PROJECT_MODIFICATIONS = 'workflows-project-modifications',
    WORKFLOW_INCREMENTAL_PLANNING  = 'workflows-incremental-planning',
}

export enum AssumptionsParamsReviewSection {
    DETAILS         = 'details',
    TIMELINE        = 'timline',
    SA_STATEMENT    = 'strategic-assumptions',
    ALL_ASSUMPTIONS = 'all',
}

export enum UserProfileParamsReviewSection {
    PROFILE = 'profile',
}

export enum CurrentUserProfileParamsReviewSection {
    DETAILS  = 'details',
    PROJECTS = 'projects',
    ORGS     = 'organizations',
}

export enum ProjectManagementParamsSettingsSection {
    DETAILS  = 'details',
    TEAM     = 'team',
    TIMELINE = 'timeline'
}

export declare module RouterLocationParams {

    interface ProjectSpecificPage {
        projectId: Uuid,
    }

    interface ProjectAndCategorySpecificPage {
        projectId: Uuid,
        categoryId: Uuid,
    }

    interface AdminResourceManager {
        type: AdminResourceManagerTypeParams,
        section: AdminObjectiveManagerParamsBySection,
        entityId: Uuid,
    }

    interface AdminTimingManager {
        section?: AdminMilestoneManagerParamsBySection,
        entityId?: Uuid,
    }

    interface AdminObjectiveManager {
        section: AdminObjectiveManagerParamsBySection,
        entityId: Uuid,
    }

    interface AdminActivityManager {
        section: AdminActivityManagerParamsBySection,
        entityId: Uuid,
    }

    interface ObjectiveSelection {
        setType: ObjectiveParamsSelectionSetType,
        categoryId: Uuid,
    }

    interface ObjectiveReview {
        section: ObjectiveParamsReviewSection,
    }

    interface PlanningArea {
        section: PlanningAreaParamsSection,
    }

    interface AssumptionsReview {
        section: AssumptionsParamsReviewSection,
    }

    interface UserProfile extends UserContextType {
        section?: UserProfileParamsReviewSection,
    }

    interface CurrentUserProfile {
        section?: CurrentUserProfileParamsReviewSection,
    }

    interface ProjectManagementSettings {
        section?: ProjectManagementParamsSettingsSection,
    }
}
